import type { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';

import type {
  CategoryData,
  CategoryDef,
  CategoriesDataParams,
  ResourceGroupTagFixRequest,
  ResourceGroupTagFixResult,
  ResourceGroupTagSuggestions,
} from '../types';

export class ScorecardsClient {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor({
    discoveryApi,
    fetchApi,
  }: {
    discoveryApi: DiscoveryApi;
    fetchApi: FetchApi;
  }) {
    this.discoveryApi = discoveryApi;
    this.fetchApi = fetchApi;
  }

  async getCategories(): Promise<CategoryDef[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('watchtower-api');
    const response = await this.fetchApi.fetch(`${baseUrl}/categories`);
    if (!response.ok) {
      throw new Error(`Failed to fetch categories: ${response.statusText}`);
    }
    return response.json();
  }

  async getCategoriesData(params: CategoriesDataParams): Promise<CategoryData[]> {
    const { teamIds, startDate, endDate, aggregateBy = 'month' } = params;
    const baseUrl = await this.discoveryApi.getBaseUrl('watchtower-api');
    const response = await this.fetchApi.fetch(`${baseUrl}/categories-data`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        team_ids: teamIds,
        start_date: startDate,
        end_date: endDate,
        aggregate_by: aggregateBy,
      }),
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch categories data: ${response.statusText}`);
    }
    return response.json();
  }

  async fixResourceGroupTags(
    request: ResourceGroupTagFixRequest,
  ): Promise<ResourceGroupTagFixResult[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('watchtower-api');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/resource-group-tags/fix`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      },
    );
    if (!response.ok) {
      const body = await response.json().catch(() => undefined);
      throw new Error(
        body?.error?.message ??
          `Failed to apply tags: ${response.status} ${response.statusText}`,
      );
    }
    const { results } = (await response.json()) as {
      results: ResourceGroupTagFixResult[];
    };
    return results;
  }

  async getResourceGroupTagSuggestions(
    subscriptionIds: string[],
  ): Promise<ResourceGroupTagSuggestions> {
    const baseUrl = await this.discoveryApi.getBaseUrl('watchtower-api');
    const query = new URLSearchParams(
      subscriptionIds.map(id => ['subscription_id', id]),
    );
    const response = await this.fetchApi.fetch(
      `${baseUrl}/resource-group-tags/suggestions?${query}`,
    );
    if (!response.ok) {
      throw new Error(
        `Failed to fetch tag suggestions: ${response.status} ${response.statusText}`,
      );
    }
    const { suggestions } = (await response.json()) as {
      suggestions: ResourceGroupTagSuggestions;
    };
    return suggestions;
  }
}
